<template>
  <div
    v-show="currentFob"
    :id="currentFob.text"
    class="flyout-container"
    @keydown.esc.prevent="emit('close')"
    @keydown.arrow-down.prevent="onKeydownArrowDown"
    @keydown.arrow-up.prevent="onKeydownArrowUp"
    @keydown.tab.prevent="onKeydownTab"
    @keydown.arrow-right.prevent="onKeydownArrowRight($event, currentFob.text)"
    @keydown.arrow-left.prevent="onKeydownArrowLeft($event, currentFob.text)"
  >
    <div v-show="isActive" v-if="currentFob.children.length" class="grid-container" :data-testid="`flyout-${currentFob.text}`">
      <div class="nav-flyout-wrapper grid-x">
        <ul v-for="(children, childIndex) in currentFob.children" :key="childIndex" class="category-cell grid-y">
          <template v-for="(group, groupIndex) in children" :key="groupIndex">
            <li
              v-if="group.children || isImageContent(group)"
              class="category-group"
              :class="{ 'flyout-image-wrapper': isImageContent(group) }"
              :data-id="group.id"
            >
              <template v-if="isImageContent(group)">
                <component
                  :is="group.imageLink ? Link : 'div'"
                  :href="group.imageLink"
                  :data-tracking="trackingObjToString(group.imageLinkTracking)"
                  class="image-link"
                  @click="
                    group.imageLink &&
                    tagNavLinkClick(isMcom, {
                      categoryId: getCategoryIdForAnalytics(null, group.id, group.imageLink),
                      context,
                      topCategoryName: flyoutCategoryName,
                      tracking: group.imageLinkTracking,
                      clickableElement: 'Shop Now',
                      col: childIndex + 1,
                      pos: getLinkPositionInColumn($event),
                      ...(!isMcom && {
                        subCategory1Name: group.text,
                        subCategory2Name: 'image' /* request from analytics */,
                      }),
                    })
                  "
                >
                  <Picture :image-url="group.src" :alt="group.text" :using-scene7="false" />
                </component>
                <Link
                  v-if="group.url && group.text"
                  class="menu-link-heavy under-image-link"
                  :href="group.url"
                  :data-tracking="trackingObjToString(group.linkTracking)"
                  @click="
                    tagNavLinkClick(isMcom, {
                      categoryId: getCategoryIdForAnalytics(null, group.id, group.url),
                      context,
                      topCategoryName: flyoutCategoryName,
                      tracking: group.linkTracking,
                      clickableElement: group.text,
                      col: childIndex + 1,
                      pos: getLinkPositionInColumn($event),
                      ...(!isMcom && {
                        subCategory1Name: group.text,
                      }),
                    })
                  "
                >
                  {{ group.text }}
                </Link>
              </template>

              <template v-else-if="group.children">
                <Link
                  v-if="group.url && group.text"
                  class="menu-link-heavy subcategory-header-link"
                  :class="{ 'subcategory-link': group.children.length }"
                  :style="group.color && { color: group.color, borderColor: group.color }"
                  :href="group.url"
                  :data-tracking="trackingObjToString(group.tracking)"
                  :data-text="group.text"
                  @click="
                    tagNavLinkClick(isMcom, {
                      categoryId: getCategoryIdForAnalytics(null, group.id, group.url),
                      context,
                      topCategoryName: flyoutCategoryName,
                      tracking: group.tracking,
                      clickableElement: group.text,
                      col: childIndex + 1,
                      pos: getLinkPositionInColumn($event),
                      ...(!isMcom && {
                        subCategory1Name: group.text,
                      }),
                    })
                  "
                >
                  <span class="text">{{ group.text }}</span>
                  <i class="chevron-small-right-dark-medium"></i>
                </Link>
                <h5
                  v-else-if="group.text"
                  class="heavy"
                  :data-text="group.text"
                  :style="group.color && { color: group.color, borderColor: group.color }"
                >
                  <ToysIcon
                    v-if="mounted && isMcom && isToys(currentFob) && childIndex === 0 && groupIndex === 0"
                    :img-style="{ marginTop: '-7px' }"
                    big
                  />
                  <span v-else>{{ group.text }}</span>
                </h5>

                <ul class="links grid-y">
                  <li v-for="(link, index) in group.children" :key="link.id + index">
                    <Link
                      :id="link.id"
                      :href="link.url"
                      class="menu-link-sm link"
                      :data-tracking="trackingObjToString(link.tracking)"
                      :data-text="link.text"
                      :style="link.color && { color: link.color, borderColor: link.color }"
                      @click="
                        tagNavLinkClick(isMcom, {
                          categoryId: getCategoryIdForAnalytics(link.id, group.id, link.url),
                          context,
                          topCategoryName: flyoutCategoryName,
                          tracking: link.tracking,
                          clickableElement: link.text,
                          col: childIndex + 1,
                          pos: getLinkPositionInColumn($event),
                          ...(!isMcom && {
                            subCategory1Name: group.text,
                            subCategory2Name: link.text,
                          }),
                        })
                      "
                    >
                      <ToysIcon
                        v-if="mounted && isMcom && index === 0 && isToysCategory({ text: link.text, ignoreIdMatch: true, exactTextMatch: true })"
                        big
                      />
                      <span v-else>{{ link.text }}</span>
                    </Link>
                  </li>
                </ul>
              </template>
            </li>
          </template>
        </ul>
      </div>
    </div>
  </div>
</template>

<script setup>
/* v8 ignore file -- @preserve */
import { useMounted } from '@vueuse/core';
import Picture from '@nuxt-common/atomic-ui/app/components/picture/Picture';

import Link from '../common/Link';
import ToysIcon from '../dumb/ToysIcon.mcom';
import { useFlyout } from '~/composables/accessibility/useFlyout';
import { tagNavLinkClick } from '#shared/features/analytics/navigationTags';
import { trackingObjToString } from '~/utils/restoreStateFromDom';
import isToysCategory from '~/utils/isToysCategory';
import getCategoryId from '~/utils/getCategoryId';

const mounted = useMounted();
const envProps = useEnvProps();
const pageData = usePageData();
const { context } = pageData.navigation;
const isMcom = envProps.isMcom;

const emit = defineEmits(['close']);

const props = defineProps({
  flyoutCategoryName: {
    type: String,
    required: true,
  },
  isActive: {
    type: Boolean,
    default: false,
  },
  currentFob: {
    type: Object,
    required: true,
  },
});

const { onKeydownArrowDown, onKeydownArrowUp, onKeydownArrowRight, onKeydownArrowLeft, onKeydownTab } = useFlyout();

function isImageContent(group) {
  return Boolean(group.isImage);
}

function getLinkPositionInColumn({ currentTarget }) {
  const categoryCell = currentTarget.closest('.category-cell');
  const arrOfLinks = categoryCell.querySelectorAll('a');
  const targetIndex = Array.from(arrOfLinks).indexOf(currentTarget);

  return targetIndex + 1;
}

/**
 * Returns the category ID of the current link for analytics.
 * First it will check if the link id is numeric, and if so then it returns the id
 * Otherwise it will try to parse id from the url
 * Otherwise it will check if the group id is numeric, and if so then it returns the id
 * In the worst case it will return flyout id
 *
 * @param {string} linkId - The ID of the link.
 * @param {string} groupId - The ID of the group.
 * @param {string} url - The URL of the link.
 * @returns {string} - The category ID.
 */
function getCategoryIdForAnalytics(linkId, groupId, url) {
  const regexp = /^\d+$/;

  if (linkId && regexp.test(linkId)) {
    return linkId;
  }

  const idInUrlMatch = url.match(/id=(\d+)/);
  if (idInUrlMatch) {
    return idInUrlMatch[1];
  }

  if (groupId && regexp.test(groupId)) {
    return groupId;
  }

  return props.currentFob.id;
}

function isToys(link) {
  return isToysCategory({
    id: getCategoryId(link),
    text: link.text,
  });
}
</script>

<style lang="scss" scoped>
@import '~/assets/scss/mixins';

.flyout-container {
  position: absolute;
  width: 100%;
  top: calc(100% + 1px);
  z-index: 1002;
  background-color: $white;
}

.grid-container {
  position: relative;
  @include padding-top-m;
  @include padding-bottom-m;

  @include breakpoint(mlarge up) {
    padding-left: 54px;
    padding-right: 54px;
  }
}

h5 {
  margin-bottom: 16px;
}

.subcategory-link {
  margin-bottom: 16px;
  display: block;
}

.nav-flyout-wrapper {
  gap: 48px;
  flex-wrap: nowrap;
}

.category-cell {
  gap: 32px;
  flex-basis: 20%;
  max-width: 158px;
  @include breakpoint(mlarge up) {
    max-width: 236px;
  }

  &.has-image {
    gap: 16px;
  }
}

.links {
  gap: 12px;
}

.link,
.image-link {
  display: block;
}

.under-image-link {
  display: block;
  margin-top: 16px;
}

.image {
  width: 100%;
}

.subcategory-header-link {
  display: flex;
  align-items: center;

  .text {
    width: fit-content;
  }
}
</style>

<style lang="scss" brand="mcom" scoped>
.link {
  color: $dashboard-color;
}

.sale-links {
  .link {
    color: $promotions-color;
    border-color: $promotions-color;
  }
}
</style>

<style lang="scss" brand="bcom" scoped>
.link {
  font-weight: 300px;
}

.flyout-image-wrapper:hover + .category-group {
  text-decoration: underline;
}
</style>
